/**
 * @file Display helpers for the admin HI-alert review surface.
 * @module pages/admin/hi-alerts/hiAlertLabels
 *
 * These format only category labels, lifecycle timestamps and the internal
 * patient id for display. They never receive or produce a raw IHI, DVA file
 * number, demographics or any other identifier.
 */

import dayjs from "dayjs";

import {
	HiAlertReview,
	HiAlertReviewType,
	IHI_CONFLICT_TYPE,
	IHI_DEMOGRAPHIC_CONFLICT_TYPE,
	IHI_REVALIDATION_REVIEW_TYPE,
	KnownReviewSeverity,
	REVIEW_REASON_MERGE_ALERT_LOCAL_ACTIVE_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_ACTIVE,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_PROVISIONAL,
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_UNVERIFIED,
	REVIEW_REASON_MERGE_ALERT_VERIFIED_RETURNED_UNVERIFIED,
	REVIEW_REASON_MERGE_RESOLVED_REPLACEMENT_NOT_ACCEPTED,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_ACTIVE,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_DECEASED,
	REVIEW_REASON_MERGE_WARNING_LOCAL_ACTIVE_RETURNED_DECEASED,
	REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_3,
	REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_4,
	REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_5,
	REVIEW_REASON_RESOLVED_IHI_REPLACEMENT_NOT_ACCEPTED,
	REVIEW_SEVERITY_ALERT,
	REVIEW_SEVERITY_UNDETERMINED,
	REVIEW_SEVERITY_WARNING,
} from "../../../types/admin/hiAlerts";
import type { UserUuid } from "../../../types/identity";

/**
 * B1.3H-3W-5D — the single operator-facing presentation contract for the
 * backend-projected review severity, shared by the list column and the detail
 * drawer so the two can never diverge.
 *
 * `label` is the operator-facing text and is always rendered. `color` is a
 * supporting cue only — it never carries meaning on its own.
 */
export interface ReviewSeverityPresentation {
	/** The bounded category actually presented — never the raw backend value. */
	severity: KnownReviewSeverity;
	label: string;
	/** A shared StatusTag colour token; supports the label, never replaces it. */
	color: string;
}

const SEVERITY_PRESENTATION: Record<KnownReviewSeverity, ReviewSeverityPresentation> = {
	[REVIEW_SEVERITY_WARNING]: { severity: REVIEW_SEVERITY_WARNING, label: "Warning", color: "var(--mg-warning)" },
	[REVIEW_SEVERITY_ALERT]: { severity: REVIEW_SEVERITY_ALERT, label: "Alert", color: "var(--mg-danger)" },
	[REVIEW_SEVERITY_UNDETERMINED]: {
		severity: REVIEW_SEVERITY_UNDETERMINED,
		label: "Undetermined",
		color: "var(--mg-info)",
	},
};

/**
 * Explicit equality checks rather than an index lookup: an index lookup on a
 * plain object would resolve inherited keys (`toString`, `constructor`, ...) for
 * an unexpected backend value and hand back something that is not a presentation.
 */
const isKnownReviewSeverity = (severity: string | null | undefined): severity is KnownReviewSeverity =>
	severity === REVIEW_SEVERITY_WARNING ||
	severity === REVIEW_SEVERITY_ALERT ||
	severity === REVIEW_SEVERITY_UNDETERMINED;

/**
 * Narrow the backend-projected severity to the bounded operator-facing display
 * contract.
 *
 * The backend is authoritative: severity is derived there from the persisted
 * bounded review reason. This maps `received backend category -> label +
 * presentation state` and nothing else. It deliberately takes ONLY the severity —
 * it cannot see the review reason, requirement id, number status, record status
 * or IHI type, so it cannot re-derive or reclassify severity from them.
 *
 * A missing, null, empty or unrecognised value fails safe to `Undetermined` so an
 * unexpected projection can never blank out the column or break the drawer.
 */
export const reviewSeverityPresentation = (severity: string | null | undefined): ReviewSeverityPresentation =>
	isKnownReviewSeverity(severity)
		? SEVERITY_PRESENTATION[severity]
		: SEVERITY_PRESENTATION[REVIEW_SEVERITY_UNDETERMINED];

/** Humanise a category review-reason label (e.g. `no_match` -> `No Match`). */
export const reviewReasonLabel = (reason: string | null): string => {
	if (!reason) {
		return "—";
	}
	// CU-14ygtkj8z3m: fixed operator-readable labels naming the registration search technique.
	switch (reason) {
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_3:
			return "Registration no match — Medicare";
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_4:
			return "Registration no match — DVA full name";
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_5:
			return "Registration no match — DVA one name";
		case REVIEW_REASON_RESOLVED_IHI_REPLACEMENT_NOT_ACCEPTED:
			return "Resolved IHI — replacement not accepted";
		// CU-14ygtkj9tu1: UC.035 merge reasons name what the HI Service returned; severity and merge eligibility stay backend-owned.
		case REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_PROVISIONAL:
			return "Merge — unsupported Provisional IHI returned";
		case REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_UNVERIFIED:
			return "Merge — unsupported Unverified IHI returned";
		case REVIEW_REASON_MERGE_ALERT_VERIFIED_RETURNED_UNVERIFIED:
			return "Merge — verified IHI returned as Unverified";
		case REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_ACTIVE:
			return "Merge — Retired status stored (was Active)";
		case REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_DECEASED:
			return "Merge — Retired status stored (was Deceased)";
		case REVIEW_REASON_MERGE_WARNING_LOCAL_ACTIVE_RETURNED_DECEASED:
			return "Merge — Active IHI returned as Deceased";
		case REVIEW_REASON_MERGE_ALERT_LOCAL_ACTIVE_RETURNED_EXPIRED:
			return "Merge — Active IHI returned as Expired";
		case REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_ACTIVE:
			return "Merge — Deceased IHI returned as Active";
		case REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_EXPIRED:
			return "Merge — Deceased IHI returned as Expired";
		case REVIEW_REASON_MERGE_RESOLVED_REPLACEMENT_NOT_ACCEPTED:
			return "Merge — Resolved IHI replacement not accepted";
		default:
			return reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
	}
};

/** Format an ISO timestamp for display, or a dash when absent. */
export const formatDateTime = (value: string | null): string =>
	value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—";

/** Identify the patient by internal id only — no demographic label lookup. */
export const patientLabel = (patientUserId: UserUuid): string => `Patient #${patientUserId}`;

const TYPE_LABELS: Record<HiAlertReviewType, string> = {
	[IHI_CONFLICT_TYPE]: "IHI conflict",
	[IHI_DEMOGRAPHIC_CONFLICT_TYPE]: "Demographic conflict",
	[IHI_REVALIDATION_REVIEW_TYPE]: "Revalidation review",
};

// Explicit equality so an inherited object key can never resolve to a label (CU-14ygtkj8z2x).
const isHiAlertReviewType = (type: string): type is HiAlertReviewType =>
	type === IHI_CONFLICT_TYPE || type === IHI_DEMOGRAPHIC_CONFLICT_TYPE || type === IHI_REVALIDATION_REVIEW_TYPE;

/** Operator-facing name of a shared-surface type; fails safe for an unknown value. */
export const hiAlertTypeLabel = (type: string): string => (isHiAlertReviewType(type) ? TYPE_LABELS[type] : "Unknown");

/** The per-type backend category shown in the list: reason, alert type or decision. */
export const hiAlertCategoryLabel = (review: HiAlertReview): string => {
	if (review.type === IHI_CONFLICT_TYPE) {
		return reviewReasonLabel(review.alert_type);
	}
	if (review.type === IHI_DEMOGRAPHIC_CONFLICT_TYPE) {
		return reviewReasonLabel(review.decision);
	}
	return reviewReasonLabel(review.review_reason);
};

/** Internal-id-only patient reference(s) for a row of any surface type. */
export const hiAlertPatientLabel = (review: HiAlertReview): string => {
	if (review.type === IHI_CONFLICT_TYPE) {
		return `${patientLabel(review.assigned_user_id)} / ${patientLabel(review.conflicting_user_id)}`;
	}
	if (review.type === IHI_DEMOGRAPHIC_CONFLICT_TYPE) {
		return `${patientLabel(review.assigned_user_id)} / ${patientLabel(review.matching_user_id)}`;
	}
	return patientLabel(review.patient_user_id);
};
